"use client";

import React, { useState, useTransition } from "react";
import { verifyMfaChallenge } from "../../../app/actions/auth-custom";
import ErrorAlert from "./ErrorAlert";
import { SSO_REQUIRED_ERROR } from "../../lib/auth-constants";
import { captureProductClientEvent } from "@/lib/analytics/product-client-event";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { normalizeAuthError } from "@/lib/analytics/auth-errors";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Note } from "@/components/ui/note";

interface MfaChallengeFormProps {
  pendingToken: string;
  challengeId: string;
  onSuccess: () => void;
  /** BRE-8618: where an org's own sign-in returns to, if this step is sent there. */
  returnTo: string | undefined;
  /** BRE-8955: the org requires SSO; the parent goes back to where the SSO button is. */
  onSsoRequired?: () => void;
}

export default function MfaChallengeForm({ pendingToken, challengeId, onSuccess, returnTo, onSsoRequired }: MfaChallengeFormProps) {
  const [isPending, startTransition] = useTransition();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    startTransition(async () => {
      const result = await verifyMfaChallenge(code, pendingToken, challengeId, returnTo);
      if (result.success) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_VERIFIED);
        onSuccess();
      } else if ("signInUrl" in result && result.signInUrl) {
        // BRE-8618: the org this sign-in lands in requires its own sign-in (SSO).
        window.location.assign(result.signInUrl);
      } else if ("error" in result) {
        captureProductClientEvent(ANALYTICS_EVENTS.AUTH_MFA_FAILED, { error_code: normalizeAuthError(result.error) });
        if (result.error === SSO_REQUIRED_ERROR && onSsoRequired) {
          onSsoRequired();
          return;
        }
        setError(result.error);
      }
    });
  }

  return (
    <div className="space-y-4">
      <Note size="sm">
        Enter the 6-digit code from your authenticator app.
      </Note>

      {error && <ErrorAlert message={error} />}

      <form onSubmit={handleSubmit} className="space-y-3">
        <label htmlFor="mfa-challenge-code" className="sr-only">Authenticator code</label>
        <Input
          textShortcuts={false}
          id="mfa-challenge-code"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          placeholder="Enter 6-digit code"
          required
          inputMode="numeric"
          pattern="^\d{6}$"
          autoComplete="one-time-code"
          autoFocus
          testId="mfa-challenge-code-input"
          variant="onboarding"
          fontSize="sm"
          inputClassName="px-0 text-center tracking-[0.5em] font-mono"
        />
        <Button
          type="submit"
          disabled={isPending || code.length !== 6}
          data-testid="mfa-challenge-submit-button"
          className="w-full"
        >
          {isPending ? "Verifying..." : "Verify"}
        </Button>
      </form>
    </div>
  );
}
